import type { LayoutConfig, LayoutElement } from './layout';
import type { MapSpot } from './map';

export type SpotItem = {
  readonly kind: 'spot';
  readonly gridRow: number;
  readonly gridCol: number;
  readonly rowSpan: 1;
  readonly colSpan: 1;
  readonly spot: MapSpot;
};

export type ElementItem = {
  readonly kind: 'element';
  readonly gridRow: number;
  readonly gridCol: number;
  readonly rowSpan: number;
  readonly colSpan: number;
  readonly element: LayoutElement;
};

export type GridItem = SpotItem | ElementItem;

export type MapGrid = {
  readonly rows: number;
  readonly cols: number;
  readonly items: readonly GridItem[];
};

const cellKey = (gridRow: number, gridCol: number): string => `${gridRow}:${gridCol}`;

// A stale gridRows never hides a spot: the grid grows to whatever it must hold.
export function mapGrid(layout: LayoutConfig, spots: readonly MapSpot[]): MapGrid {
  const placed = [
    ...layout.elements.map((e) => [e.gridRow, e.gridCol]),
    ...spots.map((s) => [s.gridRow, s.gridCol]),
  ];
  const rows = Math.max(layout.gridRows, ...placed.map(([row]) => row as number));
  const cols = Math.max(layout.gridCols, ...placed.map(([, col]) => col as number));
  return { rows, cols, items: [...elementItems(layout.elements), ...spotItems(spots)] };
}

// Neighbouring cells of a kind draw as one element the floor can read. See §6.
function elementItems(elements: readonly LayoutElement[]): ElementItem[] {
  const byCell = new Map(
    elements.map((element) => [cellKey(element.gridRow, element.gridCol), element]),
  );
  const taken = new Set<string>();
  const items: ElementItem[] = [];
  const matches = (row: number, col: number, kind: LayoutElement['kind']): boolean => {
    const key = cellKey(row, col);
    return byCell.get(key)?.kind === kind && !taken.has(key);
  };

  for (const element of elements) {
    if (taken.has(cellKey(element.gridRow, element.gridCol))) continue;
    let colSpan = 1;
    while (matches(element.gridRow, element.gridCol + colSpan, element.kind)) colSpan += 1;
    let rowSpan = 1;
    while (wholeRowMatches(matches, element, rowSpan, colSpan)) rowSpan += 1;
    for (let row = 0; row < rowSpan; row += 1) {
      for (let col = 0; col < colSpan; col += 1) {
        taken.add(cellKey(element.gridRow + row, element.gridCol + col));
      }
    }
    items.push({
      kind: 'element',
      gridRow: element.gridRow,
      gridCol: element.gridCol,
      rowSpan,
      colSpan,
      element,
    });
  }
  return items;
}

// A rectangle only grows downward while the whole next row is free and alike.
function wholeRowMatches(
  matches: (row: number, col: number, kind: LayoutElement['kind']) => boolean,
  element: LayoutElement,
  rowSpan: number,
  colSpan: number,
): boolean {
  for (let col = 0; col < colSpan; col += 1) {
    if (!matches(element.gridRow + rowSpan, element.gridCol + col, element.kind)) return false;
  }
  return true;
}

function spotItems(spots: readonly MapSpot[]): SpotItem[] {
  return spots.map((spot) => ({
    kind: 'spot',
    gridRow: spot.gridRow,
    gridCol: spot.gridCol,
    rowSpan: 1,
    colSpan: 1,
    spot,
  }));
}

export type ZoneRun = {
  readonly zoneKey: string;
  readonly gridRow: number;
  readonly rowSpan: number;
};

// One label per band of rows sharing a zone: five on the beach, two indoors.
export function zoneRuns(grid: MapGrid): ZoneRun[] {
  const runs: ZoneRun[] = [];
  for (let gridRow = 1; gridRow <= grid.rows; gridRow += 1) {
    const keys = new Set(
      grid.items
        .filter((item) => item.kind === 'spot' && item.gridRow === gridRow)
        .map((item) => (item as SpotItem).spot.zoneKey),
    );
    const [zoneKey] = keys;
    if (keys.size !== 1 || zoneKey === undefined) continue;
    const last = runs[runs.length - 1];
    if (last !== undefined && last.zoneKey === zoneKey && last.gridRow + last.rowSpan === gridRow) {
      runs[runs.length - 1] = { ...last, rowSpan: last.rowSpan + 1 };
      continue;
    }
    runs.push({ zoneKey, gridRow, rowSpan: 1 });
  }
  return runs;
}
